import { createContext, ReactNode } from "react";
import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import { Outlet, useLocation } from "react-router-dom";

import {
  AuthorizationScheme,
  AzureIntegration,
  Maybe,
  Seats,
  SlackIntegration,
} from "types/generated";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import { isSaasDistribution } from "utils/distribution";
import useErrorHandle from "hooks/useErrorHandle";
import PageLoading from "components/loading/PageLoading";

import { SettingsContextValue, UserManagementActivationStatus } from "./types";
import { GET_ACCOUNT_SETTINGS, TOGGLE_ACCOUNT_DELETION_MARK } from "./gql";
import { getAnalyticsTab } from "./getAnalyticsTab";

const isSaas = isSaasDistribution();

export const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);
SettingsContext.displayName = "SettingsContext";

type GetAccountSettingsGqlSelfHosted = {
  authorizationScheme: AuthorizationScheme;
  slackIntegration: Maybe<SlackIntegration>;
  accountCanBeDeletedAt: number;
  accountCanBeDeleted: boolean;
  markedForDeletion: boolean;
  azureIntegrations: AzureIntegration[];
  securityEmail: undefined;
  seats: Seats;
};

type GetAccountSettingsGqlSaaS = GetAccountSettingsGqlSelfHosted & {
  securityEmail: string;
};

type GetAccountSettingsGql = GetAccountSettingsGqlSaaS | GetAccountSettingsGqlSelfHosted;

export const SettingsContextProvider = ({ children }: { children?: ReactNode }) => {
  const { onError } = useTypedContext(FlashContext);
  const location = useLocation();

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.Organization,
  });

  const { loading, data, error, networkStatus } = useQuery<GetAccountSettingsGql>(
    GET_ACCOUNT_SETTINGS,
    {
      variables: {
        isSaas,
      },
      onError,
    }
  );

  const [toggleAccountDeletionMark, { loading: loadingMarking }] = useMutation(
    TOGGLE_ACCOUNT_DELETION_MARK,
    {
      refetchQueries: [{ query: GET_ACCOUNT_SETTINGS, variables: { isSaas } }],
    }
  );

  const toggleForDeletionMark = () => {
    trackSegmentAnalyticsEvent(
      data?.markedForDeletion ? "Mark for Deletion Click" : "Do not Delete Click",
      {
        tab: getAnalyticsTab(location.pathname),
      }
    );
    toggleAccountDeletionMark().then().catch(onError);
  };

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  const isLoading = loading && networkStatus !== NetworkStatus.refetch;

  if (isLoading || !data) {
    return <PageLoading />;
  }

  const activationStatus =
    data?.authorizationScheme === AuthorizationScheme.ManagedUsers
      ? UserManagementActivationStatus.ACTIVE
      : UserManagementActivationStatus.INACTIVE;

  const settingsContextValue = {
    activationStatus,
    seats: data.seats,
    markedForDeletion: data.markedForDeletion,
    toggleForDeletionMark,
    accountCanBeDeleted: data.accountCanBeDeleted,
    accountCanBeDeletedAt: data.accountCanBeDeletedAt,
    securityEmail: data.securityEmail,
    loadingMarking,
  };

  return (
    <SettingsContext.Provider value={settingsContextValue}>
      {children || <Outlet />}
    </SettingsContext.Provider>
  );
};
